import FormFieldCalendar from '@/components/FormFieldCalendar';
import FormFieldComponent from '@/components/FormFieldComponent';
import ToolBar from '@/components/ToolBar';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { DeliveryInvoiceBillingInformationTable } from './DeliveryInvoiceBillingInformationTable';
import { useEffect, useState } from 'react';
import { CircleCheckBig, SquareCheckBig, SquarePen } from 'lucide-react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useGetDeliveryInvoice, useUpdateDeliveryInvoiceMutation } from '@/services/deliveryService';
import { formatCurrency, genUniqueId, convertISODateToDDMMYYYY, convertISOToTime } from '@/lib/utils';
import { DeliveryInvoiceFormSchema, DeliveryInvoiceFormSchemaType } from './deliveryInvoice.schema';
import { DeliveryInvoiceStatus, DeliveryInvoiceEditRequest, BillingItemForEdit } from '@/types/deliveryInvoice.types';
import AlertBox from '@/components/AlertBox';
import Loader from '@/components/Loader';
import { NotFoundRedirectionWrapper } from '@/components/NotFoundRedirectWrapper';
import { toast } from '@/components/ui/use-toast';
import { displayError } from '@/lib/error';
import { zodResolver } from '@hookform/resolvers/zod';
import ApplyPaymentModal from '@/components/ApplyPaymentModal';
import { buildInvoiceRoute } from './invoiceRouteHelpers';
import { canManageDeliveryInvoice } from './deliveryInvoiceAccess';
import { hasFeature } from '@/services/featureService';
import { useUserStore } from '@/stores/user';
import {
  DeliveryInvoiceDisplayStatus,
  getDeliveryInvoiceStatusBadge,
  mapApiStatusToDisplay,
} from './deliveryInvoiceStatus';
import { DeliveryInvoiceStatusBadge } from './DeliveryInvoiceStatusBadge';

export { DeliveryInvoiceStatusBadge } from './DeliveryInvoiceStatusBadge';

export const DeliveryInvoiceView = ({ mode }: { mode: 'view' | 'edit' }) => {
  const navigate = useNavigate();
  const { invoiceNumber } = useParams();
  const [searchParam] = useSearchParams();

  const form = useForm<DeliveryInvoiceFormSchemaType>({
    mode: 'onBlur',
    resolver: zodResolver(DeliveryInvoiceFormSchema),
  });

  const updateInvoiceMutation = useUpdateDeliveryInvoiceMutation();
  const { control, reset, setValue } = form;
  const { isValid } = form.formState;
  const status = useWatch({ control, name: 'status' });

  const userStore = useUserStore();
  const [refreshKey, setRefreshKey] = useState(0);
  const disabledStyleClass = '!border-[#CAC8C8] !bg-[#F5F5F5] !opacity-100';
  const { data, isLoading, isError } = useGetDeliveryInvoice(invoiceNumber, refreshKey);
  const canEditInvoice = canManageDeliveryInvoice(userStore.user);
  const hasApplyPaymentFeature = hasFeature('accounting.applyPayment');

  const [billingItems, setBillingItems] = useState<BillingItemForEdit[]>([]);
  const [isAlertBoxOpen, setIsAlertBoxOpen] = useState(false);
  const [workOrderId, setWorkOrderId] = useState<string | null>(null);
  const [openApplyPaymentModal, setOpenApplyPaymentModal] = useState(false);

  useEffect(() => {
    if (!data) return;

    reset({
      invoiceNumber: data.invoiceNumber ?? '',
      clientName: data.clientName ?? '',
      caseName: data.caseName ?? null,
      personServed: data.personServed ?? null,
      fileReferenceNumber: data.fileReferenceNumber ?? null,
      contactName: data.clientContactName ?? '',
      billingDate: data.billingDate ? new Date(data.billingDate) : undefined,
      status: mapApiStatusToDisplay(data.invoiceStatus),
    });

    setBillingItems(
      data.billingItems?.map((item) => ({
        id: genUniqueId(),
        billingItemId: item.billingItemId ?? null,
        itemId: item.code,
        code: item.code,
        description: item.description ?? '',
        amount: item.amount,
        isAutoCalculated: item.isAutoCalculated ?? false,
      })) ?? []
    );

    setValue('totalAmount', formatCurrency(data.totalBillingAmount ?? 0));
  }, [data, reset, setValue]);

  useEffect(() => {
    if (mode !== 'edit') return;

    const previewTotal = billingItems.reduce((acc, item) => acc + (item.amount || 0), 0);
    setValue(
      'totalAmount',
      billingItems.length ? formatCurrency(previewTotal) : formatCurrency(0)
    );
  }, [billingItems, setValue, mode]);

  useEffect(() => {
    const workOrderIdParam = searchParam.get('workOrderId');
    setWorkOrderId(workOrderIdParam);
  }, [searchParam]);

  useEffect(() => {
    if (mode === 'edit' && !isLoading && !canEditInvoice && invoiceNumber) {
      navigate(buildInvoiceRoute(invoiceNumber, 'view', workOrderId));
    }
  }, [mode, isLoading, canEditInvoice, invoiceNumber, workOrderId, navigate]);

  const saveHandler = (statusUpdate?: DeliveryInvoiceStatus) => {
    if (!data?.invoiceId) return;

    const updatePayload: DeliveryInvoiceEditRequest = {};

    if (!statusUpdate) {
      const currentBillingItemIds = billingItems.filter((item) => item.billingItemId).map((item) => item.billingItemId);

      const billingItemsToCreate = billingItems
        .filter((item) => !item.billingItemId)
        .map((item) => ({
          code: item.code,
          description: item.description,
          amount: item.amount,
          isAutoCalculated: item.isAutoCalculated ?? false,
        }));

      const billingItemsToUpdate: {
        billingItemId: string;
        code: string;
        amount: number;
        description?: string;
      }[] = [];

      billingItems.forEach((item) => {
        if (!item.billingItemId) {
          return;
        }

        const originalItem = data.billingItems?.find((orig) => orig.billingItemId === item.billingItemId);
        if (!originalItem) return;

        const hasChanged =
          Number(originalItem.amount) !== Number(item.amount) ||
          originalItem.code !== item.code ||
          (originalItem.description ?? '') !== (item.description ?? '');

        if (hasChanged) {
          billingItemsToUpdate.push({
            billingItemId: item.billingItemId,
            code: item.code,
            amount: item.amount,
            description: item.description,
          });
        }
      });

      const billingItemsToDelete = data.billingItems
        ?.filter((orig) => orig.billingItemId && !currentBillingItemIds.includes(orig.billingItemId))
        .map((item) => item.billingItemId!)
        .filter(Boolean);

      if (billingItemsToCreate.length > 0) {
        updatePayload.addBillingItems = billingItemsToCreate;
      }
      if (billingItemsToUpdate.length > 0) {
        updatePayload.updateBillingItems = billingItemsToUpdate;
      }
      if (billingItemsToDelete && billingItemsToDelete.length > 0) {
        updatePayload.removeBillingItemIds = billingItemsToDelete;
      }
    }

    if (statusUpdate && statusUpdate !== data.invoiceStatus) {
      updatePayload.status = statusUpdate;
    }

    if (
      !updatePayload.addBillingItems &&
      !updatePayload.updateBillingItems &&
      !updatePayload.removeBillingItemIds &&
      !updatePayload.status
    ) {
      if (!statusUpdate) {
        setIsAlertBoxOpen(false);
        toast({
          description: 'No changes to save',
        });
      }
      return;
    }

    updateInvoiceMutation.mutate(
      {
        invoiceId: data.invoiceId,
        data: updatePayload,
      },
      {
        onSuccess: () => {
          if (mode === 'edit') {
            navigate(buildInvoiceRoute(invoiceNumber!, 'view', workOrderId));
          }
          toast({
            description: 'Invoice updated successfully',
          });
          setIsAlertBoxOpen(false);
          setRefreshKey((prev) => prev + 1);
        },
        onError: (error) => {
          setIsAlertBoxOpen(false);
          displayError(error);
        },
      }
    );
  };

  const displayStatus = (status ?? mapApiStatusToDisplay(data?.invoiceStatus)) as DeliveryInvoiceDisplayStatus;
  const statusBadgeConfig = getDeliveryInvoiceStatusBadge(data?.invoiceStatus);

  return (
    <NotFoundRedirectionWrapper itemName="Invoice" isItemNotFound={isError}>
      <Loader className="w-full scrollbar-hide flex gap-6 flex-col" isLoading={isLoading}>
        <div className="w-full flex flex-row gap-2">
          <ToolBar
            title={`W/O: ${data?.workOrderNumber} - Invoice`}
            secondaryTitle={
              <div className="flex flex-col gap-1">
                <DeliveryInvoiceStatusBadge key={displayStatus} status={statusBadgeConfig} />
                {data?.modifiedAt && data?.modifiedBy && (
                  <span className="text-xs font-normal text-placeholder">{`Updated: ${convertISODateToDDMMYYYY(data.modifiedAt)} ${convertISOToTime(data.modifiedAt)} by ${data.modifiedBy}`}</span>
                )}
              </div>
            }
            additionalButton={
              <div className="flex flex-row gap-6 items-center">
                {mode === 'view' && (
                  <>
                    {canEditInvoice && (
                      <Button
                        key="edit"
                        type="button"
                        variant="outline"
                        size="icon"
                        className="h-8 w-8"
                        aria-label="Edit invoice"
                        onClick={() => navigate(buildInvoiceRoute(invoiceNumber!, 'edit', workOrderId))}
                      >
                        <SquarePen className="text-placeholder" />
                      </Button>
                    )}

                    {displayStatus === DeliveryInvoiceDisplayStatus.REVIEWED && canEditInvoice && (
                      <Loader isLoading={updateInvoiceMutation.isPending}>
                        <Button
                          variant="outline"
                          className="h-[36px]"
                          onClick={() => {
                            saveHandler(DeliveryInvoiceStatus.CONFIRMED);
                          }}
                        >
                          <div className="flex flex-row gap-3 items-center">
                            <SquareCheckBig className="h-4 w-4" />
                            Mark as Confirmed
                          </div>
                        </Button>
                      </Loader>
                    )}

                    {hasApplyPaymentFeature &&
                      displayStatus === DeliveryInvoiceDisplayStatus.CONFIRMED &&
                      ['ADMIN', 'ACCOUNTANT'].includes(userStore.user?.roleId ?? '') && (
                        <Button
                          className="h-[36px] bg-[#1B8652] hover:bg-[#1B8652]"
                          onClick={() => {
                            setOpenApplyPaymentModal(true);
                          }}
                          key={refreshKey}
                        >
                          <div className="flex flex-row gap-3 items-center">
                            <CircleCheckBig className="h-4 w-4" />
                            Apply Payment
                          </div>
                        </Button>
                      )}
                  </>
                )}
              </div>
            }
          />
        </div>

        <FormProvider {...form}>
          <div className="grid grid-cols-12 gap-6 w-full">
            <div className="col-span-12 md:col-span-6 space-y-4">
              <FormFieldComponent
                name="invoiceNumber"
                control={control}
                disabled
                label="Invoice #"
                defaultValue={data?.invoiceNumber ?? ''}
                disabledStyle={disabledStyleClass}
              />
              <FormFieldComponent
                name="clientName"
                control={control}
                disabled
                defaultValue={data?.clientName ?? ''}
                label="Client Name"
                disabledStyle={disabledStyleClass}
              />
              <FormFieldComponent
                name="caseName"
                control={control}
                disabled
                defaultValue={data?.caseName ?? ''}
                label="Case Name"
                disabledStyle={disabledStyleClass}
              />
              <FormFieldComponent
                name="personServed"
                control={control}
                disabled
                label="Person Served"
                defaultValue={data?.personServed ?? ''}
                disabledStyle={disabledStyleClass}
              />
              <FormFieldComponent
                name="fileReferenceNumber"
                control={control}
                disabled
                label="File Number"
                defaultValue={data?.fileReferenceNumber ?? ''}
                disabledStyle={disabledStyleClass}
              />
              <FormFieldComponent
                name="contactName"
                control={control}
                disabled
                label="Contact Name"
                disabledStyle={disabledStyleClass}
              />
            </div>

            <div className="col-span-12 md:col-span-6 border-[1px] border-[#CAC8C8] h-full rounded-md">
              <div className="bg-[#f5f5f5] rounded-md p-6 font-bold">Billing Information</div>
              <div>
                <DeliveryInvoiceBillingInformationTable
                  mode={mode}
                  billingItems={billingItems}
                  setBillingItems={setBillingItems}
                  key={`delivery-invoice-billing-information-table-${billingItems.length}`}
                />
              </div>
            </div>

            <div className="col-span-6 md:col-span-6 space-y-4">
              <FormFieldCalendar
                name="billingDate"
                control={control}
                label="Billing Date"
                disabled
                disabledStyle={disabledStyleClass}
                placeholder="Invoice not confirmed"
              />
            </div>
            <div className="col-span-6 md:col-span-6 space-y-4">
              <FormFieldComponent
                name="totalAmount"
                control={control}
                disabled
                label="Total Billing Amount"
                disabledStyle={disabledStyleClass}
              />
            </div>
          </div>
        </FormProvider>

        {openApplyPaymentModal && (
          <ApplyPaymentModal
            open={openApplyPaymentModal}
            onOpenChange={setOpenApplyPaymentModal}
            invoices={
              data?.invoiceNumber
                ? [
                    {
                      invoiceNumber: data.invoiceNumber,
                      invoiceAmount: data.totalBillingAmount ?? 0,
                      balance: data.totalBillingAmount ?? 0,
                    },
                  ]
                : []
            }
            onSuccess={(paymentData) => {
              setOpenApplyPaymentModal(false);
              const remainingBalance = paymentData.invoices[0]?.balance ?? 0;
              if (remainingBalance <= 0) {
                saveHandler(DeliveryInvoiceStatus.PAID);
              } else {
                setRefreshKey((prev) => prev + 1);
              }
            }}
          />
        )}

        {mode === 'edit' && canEditInvoice && (
          <div className="flex flex-row gap-3 w-full justify-end pb-6 pt-10">
            <Button
              variant="outline"
              className="h-[36px] w-[112px]"
              onClick={() => navigate(`/delivery${workOrderId ? `?workOrderId=${workOrderId}` : ''}`)}
            >
              Cancel
            </Button>
            <AlertBox
              triggerButton={
                <Button
                  variant="default"
                  className="h-[36px] w-[103px] hover:bg-[#12375C]"
                  disabled={!isValid || updateInvoiceMutation.isPending}
                  onClick={() => setIsAlertBoxOpen(true)}
                  data-testid="trigger-save-button"
                >
                  Save
                </Button>
              }
              title="Are you sure?"
              description="Would you like to update invoice details?"
              cancelBtnLabel="No"
              confirmBtnLabel="Yes"
              open={isAlertBoxOpen}
              isLoading={updateInvoiceMutation.isPending}
              cancelBtnClick={() => setIsAlertBoxOpen(false)}
              confirmBtnClick={() => saveHandler()}
            />
          </div>
        )}
      </Loader>
    </NotFoundRedirectionWrapper>
  );
};
